import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Download, CheckCircle2, Share, Plus, Smartphone } from 'lucide-react';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

/** Installazione dell'app sul telefono o sul computer. */
const InstallAppCard = () => {
  const [installable, setInstallable] = useState(!!window.deferredInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onReady = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); };
    window.addEventListener('pwa-installable', onReady);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('pwa-installable', onReady);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const install = async () => {
    const prompt = window.deferredInstallPrompt;
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.deferredInstallPrompt = null;
    setInstallable(false);
    if (outcome === 'accepted') {
      setInstalled(true);
      toast.success('App installata, la trovi tra le tue app');
    } else {
      toast('Installazione annullata');
    }
  };

  return (
    <Card data-testid="install-app-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-primary" /> Installa l'app
        </CardTitle>
        <CardDescription>
          Aggiungila alla schermata del telefono o al computer: si apre a tutto schermo, senza barra del browser,
          e riceve le notifiche come un'app vera.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {installed ? (
          <div className="flex items-center gap-2 text-sm" data-testid="install-state-installed">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            App già installata su questo dispositivo.
          </div>
        ) : installable ? (
          <Button onClick={install} className="min-h-[44px]" data-testid="install-app-btn">
            <Download className="w-4 h-4 mr-2" />Installa ora
          </Button>
        ) : isIos() ? (
          <div className="space-y-2 text-sm text-muted-foreground" data-testid="install-ios-steps">
            <p className="text-foreground font-medium">Su iPhone e iPad si installa da Safari:</p>
            <p className="flex items-center gap-2"><Share className="w-4 h-4 shrink-0" />1. Tocca Condividi nella barra in basso</p>
            <p className="flex items-center gap-2"><Plus className="w-4 h-4 shrink-0" />2. Scegli "Aggiungi a Home"</p>
            <p>3. Conferma: l'icona compare tra le tue app</p>
          </div>
        ) : (
          <div className="space-y-2 text-sm text-muted-foreground" data-testid="install-state-unavailable">
            <p>
              Il pulsante compare appena il browser è pronto. Su Chrome e Edge trovi l'installazione anche
              nell'icona a destra della barra degli indirizzi, oppure nel menu alla voce "Installa".
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}
              className="min-h-[44px]" data-testid="install-refresh-btn">
              Riprova
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InstallAppCard;
